/**
 * notification-preferences.ts — Cliente API pra pagina /notificacoes/preferencias.
 *
 * Spec docs/notificacoes/IMPLEMENTATION_SPEC_V1V2.md §3.4 / §3.8.
 *
 * Endpoints cobertos:
 * - GET    /v1/me/notification-subscriptions
 * - PATCH  /v1/me/notification-subscriptions       { items: SubscriptionUpdate[] }
 * - DELETE /v1/me/notification-subscriptions       (reset to defaults; DELETE + re-seed atomico)
 * - GET    /v1/me/notifications/export             (JSON, com fallback CSV client-side)
 * - DELETE /v1/me/notifications/history
 *
 * Para o feed e os endpoints de mark-read/dismiss/acknowledge use
 * `notifications-feed.ts` (esse arquivo eh especifico de preferencias).
 */
import type { AvisoDoAjuste } from '@/api/robo'
import type { QueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'

// `mobile` e `voice` FALTAVAM aqui: existem no backend desde as MIGRATION_258 e
// 277 e o tipo do frontend nunca os recebeu. Acrescentar so' 'robo' deixaria o
// tipo errado de um jeito novo.
export type SubscriptionChannel =
  | 'feed' | 'email' | 'webpush' | 'whatsapp' | 'slack' | 'teams' | 'sms'
  | 'inapp' | 'mobile' | 'voice' | 'robo'

export type SubscriptionSeverity =
  | 'debug' | 'info' | 'warning' | 'critical' | 'fatal'

export type SubscriptionScopeKind = 'organization' | 'tenant'

export interface Subscription {
  user_id: number
  source: string
  event_type: string | null
  channel: SubscriptionChannel
  enabled: boolean
  min_severity: SubscriptionSeverity
  quiet_hours: Record<string, unknown> | null
  scope_kind?: SubscriptionScopeKind | null
  scope_id?: number | null
}

export interface SubscriptionUpdate {
  source: string
  event_type?: string | null
  channel: SubscriptionChannel
  enabled?: boolean
  min_severity?: SubscriptionSeverity
  quiet_hours?: Record<string, unknown> | null
  scope_kind?: SubscriptionScopeKind | null
  scope_id?: number | null
}

export interface SubscriptionsListResponse {
  items: Subscription[]
}

export interface SubscriptionsUpdateResponse {
  updated_count: number
  /** Só quando algum item é do canal `robo` e a pessoa tem robô: a reação dele. */
  aviso?: AvisoDoAjuste | null
}

export interface SubscriptionsResetResponse {
  deleted_count: number
  seeded_count: number
}

export interface ExportItem {
  [key: string]: unknown
}

export interface ExportResponse {
  user_email: string
  exported_count: number
  items: ExportItem[]
}

export interface DeleteHistoryResponse {
  deleted_count: number
  archived_to_audit_trail: boolean
}

export interface QuietHoursPayload {
  start_hh_mm: string | null
  end_hh_mm: string | null
  timezone: string | null
  except_severity?: string[] | null
}

export interface QuietHoursUpdateResponse {
  status: string
}

function itemsToCsv(items: ExportItem[]): string {
  if (items.length === 0) return ''
  const allKeys = new Set<string>()
  for (const it of items) {
    Object.keys(it).forEach((k) => allKeys.add(k))
  }
  const headers = Array.from(allKeys)
  const escape = (val: unknown): string => {
    if (val == null) return ''
    const s = typeof val === 'string' ? val : JSON.stringify(val)
    if (/[",\n\r]/.test(s)) {
      return `"${s.replace(/"/g, '""')}"`
    }
    return s
  }
  const lines = [headers.join(',')]
  for (const it of items) {
    lines.push(headers.map((h) => escape(it[h])).join(','))
  }
  return lines.join('\n')
}

export const notificationPreferencesApi = {
  async listSubscriptions(): Promise<SubscriptionsListResponse> {
    const { data } = await api.get<SubscriptionsListResponse>(
      '/v1/me/notification-subscriptions',
    )
    return data
  },

  async updateSubscriptions(
    items: SubscriptionUpdate[],
  ): Promise<SubscriptionsUpdateResponse> {
    const { data } = await api.patch<SubscriptionsUpdateResponse>(
      '/v1/me/notification-subscriptions',
      { items },
    )
    return data
  },

  /**
   * Reseta as subscriptions do user pros defaults (DELETE + re-seed atomico).
   *
   * Backend faz DELETE de todas as user_notification_subscriptions do user
   * e re-seeda as defaults na MESMA transacao (rollback se seed falha).
   */
  async resetToDefaults(): Promise<SubscriptionsResetResponse> {
    const { data } = await api.delete<SubscriptionsResetResponse>(
      '/v1/me/notification-subscriptions',
    )
    return data
  },

  /**
   * Triggers download of the user's notification history.
   *
   * Backend endpoint currently returns JSON only; for CSV we convert client-side.
   * Spec §1.13 (LGPD art. 18, I — direito de acesso).
   */
  async exportHistory(format: 'csv' | 'json'): Promise<Blob> {
    const { data } = await api.get<ExportResponse>(
      '/v1/me/notifications/export',
    )
    if (format === 'json') {
      return new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      })
    }
    const csv = itemsToCsv(data.items ?? [])
    return new Blob([csv], { type: 'text/csv;charset=utf-8' })
  },

  async deleteHistory(): Promise<DeleteHistoryResponse> {
    const { data } = await api.delete<DeleteHistoryResponse>(
      '/v1/me/notifications/history',
    )
    return data
  },

  async getQuietHours(): Promise<QuietHoursPayload> {
    const { data } = await api.get<QuietHoursPayload>('/v1/me/quiet-hours')
    return data
  },

  async updateQuietHours(
    payload: QuietHoursPayload,
  ): Promise<QuietHoursUpdateResponse> {
    const { data } = await api.patch<QuietHoursUpdateResponse>(
      '/v1/me/quiet-hours',
      payload,
    )
    return data
  },
}

/**
 * Invalida TODAS as queries com prefixo ['me', ...] — useful pra mutations
 * em /me/notification-* que afetam multiplos widgets (subscriptions,
 * quiet-hours, views, etc).
 *
 * Sem isso, cada mutation só invalidava sua propria QUERY_KEY especifica,
 * e widgets vizinhos ficavam com cache stale ate refetch manual.
 */
export function invalidateMeNotificationQueries(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({
    predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'me',
  })
}
